import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey, Transaction, type EditorState } from '@tiptap/pm/state';

/**
 * One AI-suggested span the user has accepted into the editor.
 * `from` / `to` are ProseMirror positions, kept up to date on every transaction
 * so we can read the current text of the inserted span — even after the user has
 * edited around or inside it.
 */
export type AcceptedSuggestionTrack = {
	id: string;
	originalText: string;
	dimensionKey: string | null;
	conversationId: string | null;
	from: number;
	to: number;
};

type TrackerState = {
	tracks: AcceptedSuggestionTrack[];
};

const TRACK_ADD_META = 'suggestion-track-add';
const positionTrackerKey = new PluginKey<TrackerState>('suggestion-position-tracker');

/** Read the live tracked accept ranges (with mapped positions) for the given editor state. */
export function getAcceptedSuggestionTracks(state: EditorState): AcceptedSuggestionTrack[] {
	return positionTrackerKey.getState(state)?.tracks ?? [];
}

/** Meta payload shape for adding a new track in the same transaction as the insert. */
export type SuggestionTrackAddMeta = {
	id: string;
	originalText: string;
	dimensionKey: string | null;
	conversationId: string | null;
	from: number;
	to: number;
};

export const SUGGESTION_TRACK_ADD_META = TRACK_ADD_META;

export const SuggestionHighlight = Mark.create({
	name: 'suggestion-highlight',
	inclusive: false,
	excludes: '',

	addAttributes() {
		return {};
	},

	parseHTML() {
		return [{ tag: 'span[data-suggestion-highlight]' }];
	},

	renderHTML({ HTMLAttributes }) {
		return [
			'span',
			mergeAttributes(HTMLAttributes, {
				'data-suggestion-highlight': '',
				style:
					'text-decoration-line: underline; text-decoration-style: dotted; text-decoration-color: var(--chakra-colors-ai-text-underline); text-decoration-thickness: 2px; text-underline-offset: 0.18em;',
			}),
			0,
		];
	},

	addProseMirrorPlugins() {
		const markType = this.type;
		return [
			// Visual mark removal: any user edit that intersects a `suggestion-highlight`
			// mark strips the mark, so the AI underline disappears once the user "owns" the text.
			new Plugin({
				key: new PluginKey('suggestion-highlight-remover'),
				appendTransaction(transactions, _oldState, newState) {
					const docChanged = transactions.some((tr) => tr.docChanged);
					if (!docChanged) return null;

					const isInsertion = transactions.some((tr) => tr.getMeta('suggestion-highlight-insertion') === true);
					if (isInsertion) return null;

					let outTr: Transaction | null = null;

					transactions.forEach((transaction) => {
						if (!transaction.docChanged) return;
						transaction.steps.forEach((step) => {
							step.getMap().forEach((_oldStart, _oldEnd, newStart, newEnd) => {
								// Expand by 1 on each side so deletions (newStart === newEnd)
								// still catch the marked text remaining around the deletion point.
								const checkFrom = Math.max(0, newStart - 1);
								const checkTo = Math.min(newState.doc.content.size, newEnd + 1);
								newState.doc.nodesBetween(checkFrom, checkTo, (node, pos) => {
									if (!node.isText) return;
									if (node.marks.some((m) => m.type === markType)) {
										if (!outTr) outTr = newState.tr;
										outTr.removeMark(pos, pos + node.nodeSize, markType);
									}
								});
							});
						});
					});

					return outTr;
				},
			}),

			// Position tracker: keeps each accepted suggestion's character span up to date
			// across edits using ProseMirror's transaction `mapping`. Independent of the
			// visual mark above — the underline can vanish on edit while we keep tracking
			// where the suggestion text currently lives so analytics can compare it to the
			// original at submit time.
			new Plugin<TrackerState>({
				key: positionTrackerKey,
				state: {
					init: () => ({ tracks: [] }),
					apply(tr, prev) {
						const addMeta = tr.getMeta(TRACK_ADD_META) as SuggestionTrackAddMeta | undefined;

						let tracks = prev.tracks;

						if (tr.docChanged && tracks.length > 0) {
							tracks = tracks.map((t) => {
								// Mirror the visual mark behavior (`inclusive: false`):
								// inserts at either boundary land OUTSIDE the span. `assoc: 1` on
								// `from` pushes the start past a boundary insert; `assoc: -1` on
								// `to` keeps the end before a boundary insert. Inserts strictly
								// inside the span shift `to` (and never `from`) so the span grows
								// to include the typed text.
								const from = tr.mapping.map(t.from, 1);
								const to = tr.mapping.map(t.to, -1);
								return { ...t, from, to: Math.max(from, to) };
							});
						}

						if (addMeta) {
							tracks = [
								...tracks,
								{
									id: addMeta.id,
									originalText: addMeta.originalText,
									dimensionKey: addMeta.dimensionKey,
									conversationId: addMeta.conversationId,
									from: addMeta.from,
									to: addMeta.to,
								},
							];
						}

						return tracks === prev.tracks ? prev : { tracks };
					},
				},
			}),
		];
	},
});
